// Client-side billing / entitlement.
//
// The client NEVER writes `plan` — that's admin-only (see firestore.rules +
// functions/lib/entitlement.js). Here we only: ensure a base free profile
// exists, subscribe to the profile to know if the user is Pro, and kick off
// checkout via the Cloud Function callables. State flips to Pro asynchronously
// when the processor's webhook/callback lands and updates users/{uid}.
import { doc, getDoc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db, firebaseConfigured } from './firebase.js';

// Upgrade CTAs render as "Coming soon" until the checkout Cloud Functions are
// deployed — set VITE_BILLING_LIVE=true in the hosting env to enable them.
export const billingLive = import.meta.env.VITE_BILLING_LIVE === 'true';

let cachedProfile = null;
const profileListeners = new Set();

// isPro reads the cached profile synchronously. Pro is either subscription-
// managed (proExpiresAt == null, renews until canceled) or time-boxed
// (M-Pesa one-off — Pro until proExpiresAt passes).
export function isPro() {
  const p = cachedProfile;
  if (!p || p.plan !== 'pro') return false;
  if (!p.proExpiresAt) return true;
  return toMs(p.proExpiresAt) > Date.now();
}

export function getProfile() {
  return cachedProfile;
}

// Subscribe to changes in Pro status. Fires immediately with the current value.
export function onProfileChange(cb) {
  profileListeners.add(cb);
  cb(cachedProfile);
  return () => profileListeners.delete(cb);
}

function emit() {
  profileListeners.forEach((cb) => {
    try {
      cb(cachedProfile);
    } catch (err) {
      console.warn('[billing] listener error:', err);
    }
  });
}

// Create the users/{uid} profile if it doesn't exist yet. Client may only ever
// create it as `free`; upgrades happen server-side.
export async function ensureProfile(user) {
  if (!firebaseConfigured || !user) return;
  const ref = doc(db, 'users', user.uid);
  try {
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      await setDoc(ref, {
        plan: 'free',
        email: user.email || null,
        createdAt: serverTimestamp(),
      });
    }
  } catch (err) {
    // A missing profile just means Pro features stay locked — never block the app.
    console.warn('[billing] ensureProfile failed:', err);
  }
}

let unsubProfile = null;

// Live-subscribe to the signed-in user's profile so isPro() stays current when
// a webhook upgrades/downgrades them mid-session. Call on sign-in.
export function watchProfile(uid) {
  stopWatchingProfile();
  if (!firebaseConfigured || !uid) return;
  unsubProfile = onSnapshot(
    doc(db, 'users', uid),
    (snap) => {
      cachedProfile = snap.exists() ? { id: snap.id, ...snap.data() } : null;
      emit();
    },
    (err) => console.warn('[billing] profile subscription error:', err),
  );
}

export function stopWatchingProfile() {
  if (unsubProfile) {
    unsubProfile();
    unsubProfile = null;
  }
  cachedProfile = null;
  emit();
}

// ── Checkout ──────────────────────────────────────────────────────────────────

async function call(name, payload) {
  if (!firebaseConfigured) throw new Error('Billing not available.');
  const { callFunction } = await import('./callable.js');
  return callFunction(name, payload);
}

// Stripe / Flutterwave both return a hosted URL we redirect to.
export async function startCheckout(processor) {
  const name = processor === 'flutterwave' ? 'createFlutterwaveCheckout' : 'createStripeCheckout';
  const data = await call(name);
  if (!data?.url) throw new Error('Checkout could not be started.');
  window.location.assign(data.url);
}

// M-Pesa is different: no redirect. We trigger an STK push to the user's phone;
// they approve in the M-Pesa menu; the callback upgrades them. The caller should
// watch onProfileChange() to detect the flip to Pro.
export async function startMpesaCheckout(phone) {
  const data = await call('mpesaStkPush', { phone });
  return data; // { checkoutRequestId, message }
}

// Stripe self-serve portal (cancel / update card / invoices).
export async function openBillingPortal() {
  const data = await call('createStripePortal');
  if (!data?.url) throw new Error('Could not open billing portal.');
  window.location.assign(data.url);
}

function toMs(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (ts instanceof Date) return ts.getTime();
  if (typeof ts === 'number') return ts;
  if (ts.seconds) return ts.seconds * 1000;
  return 0;
}
